import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  IconButton,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useParams } from "@tanstack/react-router"
import React from "react"
import { FiChevronDown, FiMessageSquare } from "react-icons/fi"
import Select from "@/components/Common/AppSelect"
import { previewDocumentPdf } from "../../../../api/documentPdf"
import {
  type TransferConfirmMMPDTO,
  type TransferDocumentMMWByIDDTO,
  TransferService,
} from "../../../../client"
import { useWarehouseInventoryBlocks } from "../../../../hooks/useWarehouseInventoryBlocks"
import WarehouseLocationSelect from "../../Modals/WarehouseLocationSelect"
import DocumentFieldRow from "../../shared/DocumentFieldRow"
import DocumentHeaderSection from "../../shared/DocumentHeaderSection"
import DocumentPageHeader from "../../shared/DocumentPageHeader"
import DocumentProductCell from "../../shared/DocumentProductCell"
import { DISCREPANCY_REASON_OPTIONS } from "../../shared/discrepancyReasons"
import { InventoryBlockReadNotice } from "../../shared/InventoryBlockReadNotice"
import {
  hasPositiveReconciliationDiscrepancy,
  normalizeNearZeroQuantity,
} from "../../shared/nearZeroQuantity"
import PrintLanguageSelect from "../../shared/PrintLanguageSelect"
import { formatLotDate } from "../../shared/warehouseLots"

type RowState = {
  itemId: string
  acceptedQty: string
  damagedQty: string
  rejectedQty: string
  discrepancyReason: string
  discrepancyComment: string
  toLocationCode: string
}

const toNumber = (value: string) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const fmtNum = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 })
  .format

export function DocumentMMPPage() {
  const params = useParams({ from: "/_layout/WMS/documents/doc-mmp/$docMmp" })
  const id = params.docMmp
  const toast = useToast()
  const qc = useQueryClient()
  const panelBg = useColorModeValue("white", "gray.800")
  const labelColor = useColorModeValue("gray.600", "gray.300")
  const tableHeadBg = useColorModeValue("gray.100", "gray.700")
  const [rows, setRows] = React.useState<RowState[]>([])
  const [isSubmitting, setSubmitting] = React.useState(false)
  const [isCancelling, setCancelling] = React.useState(false)
  const cancelRef = React.useRef<HTMLButtonElement | null>(null)
  const [isCancelOpen, setCancelOpen] = React.useState(false)

  const { data, isPending, isError, error } =
    useQuery<TransferDocumentMMWByIDDTO>({
      queryKey: ["wms", "transfers", "mmp", "detail", id],
      queryFn: () => TransferService.getDocumentMMPByID(id),
    })
  const { blockedWarehouseIds, ready: inventoryBlocksReady } =
    useWarehouseInventoryBlocks()

  React.useEffect(() => {
    if (!data) return
    setRows(
      (data.items ?? []).map((item) => ({
        itemId: item.id,
        acceptedQty: String(
          normalizeNearZeroQuantity(item.accepted_qty || item.qty || 0),
        ),
        damagedQty: String(normalizeNearZeroQuantity(item.damaged_qty || 0)),
        rejectedQty: String(normalizeNearZeroQuantity(item.rejected_qty || 0)),
        discrepancyReason: item.discrepancy_reason || "",
        discrepancyComment: item.discrepancy_comment || "",
        toLocationCode: item.to_location_code || "",
      })),
    )
  }, [data])
  const warehouseIDs = [data?.from_warehouse?.id, data?.to_warehouse?.id]
  const isInventoryBlockedForWarehouses =
    !inventoryBlocksReady ||
    warehouseIDs.some(
      (warehouseID) => !warehouseID || blockedWarehouseIds.has(warehouseID),
    )
  const inventoryBlockCancelReason =
    "Anulowanie zablokowane: trwa inwentaryzacja na magazynie źródłowym lub docelowym."

  const normalizeProduct = (p: any) => {
    if (!p) return undefined
    if (Array.isArray(p)) return p[0]
    return p
  }

  const rowErrors = React.useMemo(() => {
    return rows.reduce<Record<string, string>>((acc, row) => {
      const planned =
        data?.items?.find((item) => item.id === row.itemId)?.qty ?? 0
      const total =
        normalizeNearZeroQuantity(toNumber(row.acceptedQty)) +
        normalizeNearZeroQuantity(toNumber(row.damagedQty)) +
        normalizeNearZeroQuantity(toNumber(row.rejectedQty))
      if (Math.abs(total - planned) > 0.0001) {
        acc[row.itemId] = `Suma musi wynosić ${fmtNum(planned)}`
      }
      if (
        hasPositiveReconciliationDiscrepancy(
          toNumber(row.damagedQty),
          toNumber(row.rejectedQty),
        ) &&
        !row.discrepancyReason
      ) {
        acc[row.itemId] = "Wybierz przyczynę rozbieżności"
      }
      return acc
    }, {})
  }, [data?.items, rows])

  const handleRowChange = (itemId: string, patch: Partial<RowState>) => {
    setRows((current) =>
      current.map((row) =>
        row.itemId === itemId ? { ...row, ...patch } : row,
      ),
    )
  }

  const buildPayload = (): TransferConfirmMMPDTO => ({
    items: rows.map((row) => ({
      item_id: row.itemId,
      accepted_qty: normalizeNearZeroQuantity(toNumber(row.acceptedQty)),
      damaged_qty: normalizeNearZeroQuantity(toNumber(row.damagedQty)),
      rejected_qty: normalizeNearZeroQuantity(toNumber(row.rejectedQty)),
      discrepancy_reason: row.discrepancyReason || undefined,
      discrepancy_comment: row.discrepancyComment || undefined,
      to_location_code: row.toLocationCode || undefined,
    })),
  })

  const onConfirm = async () => {
    if (isInventoryBlockedForWarehouses) {
      toast({
        status: "warning",
        title: "Nie można rozliczyć dokumentu",
        description: inventoryBlocksReady
          ? "Potwierdzenie zablokowane: trwa inwentaryzacja lub nie udało się ustalić magazynu dokumentu."
          : "Sprawdzenie blokad magazynowych nadal trwa lub zakończyło się błędem. Ponów próbę po poprawnym odczycie.",
      })
      return
    }
    if (Object.keys(rowErrors).length > 0) {
      toast({
        status: "warning",
        title: "Popraw dane przyjęcia",
        description:
          "Co najmniej jedna pozycja ma nieprawidłowy bilans ilości.",
      })
      return
    }
    try {
      setSubmitting(true)
      await TransferService.confirmDocumentMMPByID(id, buildPayload())
      toast({ status: "success", title: "Dokument MMP został rozliczony" })
      await Promise.all([
        qc.invalidateQueries({
          queryKey: ["wms", "transfers", "mmp", "detail", id],
        }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmp"] }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmr"] }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmw"] }),
      ])
    } catch (e: any) {
      toast({
        status: "error",
        title: "Nie udało się potwierdzić MMP",
        description: e?.body?.error ?? e?.message ?? "Błąd",
      })
    } finally {
      setSubmitting(false)
    }
  }

  const onCancel = async () => {
    try {
      setCancelling(true)
      await TransferService.cancelDocumentMMPByID(id)
      toast({
        status: "success",
        title: "Dokument MMP został anulowany",
        description: "Powiązany dokument MMW wrócił do wersji roboczej.",
      })
      await Promise.all([
        qc.invalidateQueries({
          queryKey: ["wms", "transfers", "mmp", "detail", id],
        }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmp"] }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmw"] }),
      ])
    } catch (e: any) {
      toast({
        status: "error",
        title: "Nie udało się anulować MMP",
        description: e?.body?.error ?? e?.message ?? "Błąd",
      })
    } finally {
      setCancelling(false)
      setCancelOpen(false)
    }
  }

  const unconfirmMutation = useMutation({
    mutationFn: async () => {
      return TransferService.unconfirmDocumentMMPByID(id)
    },
    onSuccess: async () => {
      toast({ status: "success", title: "Dokument MMP został odblokowany" })
      await Promise.all([
        qc.invalidateQueries({
          queryKey: ["wms", "transfers", "mmp", "detail", id],
        }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmp"] }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmr"] }),
        qc.invalidateQueries({ queryKey: ["wms", "transfers", "mmw"] }),
      ])
    },
    onError: (e: any) => {
      toast({
        status: "error",
        title: "Nie udało się odblokować MMP",
        description: e?.body?.error ?? e?.message ?? "Błąd",
      })
    },
  })

  const handleUnconfirm = () => {
    if (isInventoryBlockedForWarehouses) {
      toast({
        status: "warning",
        title: "Nie można odblokować MMP",
        description: !inventoryBlocksReady
          ? "Sprawdzenie blokad magazynowych trwa lub zakończyło się błędem. Ponów próbę po poprawnym odczycie."
          : warehouseIDs.some((warehouseID) => !warehouseID)
            ? "Nie udało się ustalić obu magazynów dokumentu. Odśwież dokument i spróbuj ponownie."
            : "Na jednym z magazynów trwa inwentaryzacja.",
      })
      return
    }
    unconfirmMutation.mutate()
  }

  const fmtDate = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : "—"

  if (isPending) {
    return (
      <Flex justify="center" align="center" w="full" h="60vh">
        <Spinner size="xl" color="main.accent" />
      </Flex>
    )
  }

  if (isError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        Nie udało się załadować dokumentu MMP:{" "}
        {(error as any)?.message ?? "Błąd"}
      </Alert>
    )
  }

  if (!data) {
    return (
      <Flex justify="center" align="center" w="full" h="40vh">
        <Text color="gray.500">Nie znaleziono dokumentu</Text>
      </Flex>
    )
  }

  return (
    <Container
      maxW="10xl"
      py={6}
      display="flex"
      flexDirection="column"
      h="full"
      minH={0}
      overflow="hidden"
    >
      <InventoryBlockReadNotice />
      <DocumentPageHeader
        number={data.number}
        status={data.status}
        labelColor={labelColor}
        actions={
          <HStack>
            {data.status === "draft" ? (
              <Button
                colorScheme="green"
                size="sm"
                onClick={onConfirm}
                isLoading={isSubmitting}
              >
                Rozlicz MMP
              </Button>
            ) : null}

            <Menu>
              <MenuButton as={Button} size="sm" rightIcon={<FiChevronDown />}>
                Akcje
              </MenuButton>
              <MenuList>
                {data.status === "draft" ? (
                  <MenuItem
                    color="red.500"
                    onClick={() => setCancelOpen(true)}
                    isDisabled={isInventoryBlockedForWarehouses || isCancelling}
                    title={
                      isInventoryBlockedForWarehouses
                        ? inventoryBlockCancelReason
                        : undefined
                    }
                  >
                    {isCancelling ? "Anulowanie..." : "Anuluj"}
                  </MenuItem>
                ) : null}

                {data.status === "confirmed" ||
                data.status === "partially_confirmed" ? (
                  <MenuItem
                    color="orange.500"
                    onClick={handleUnconfirm}
                    isDisabled={
                      isInventoryBlockedForWarehouses ||
                      unconfirmMutation.isPending
                    }
                  >
                    {unconfirmMutation.isPending
                      ? "Odblokowywanie..."
                      : "Odblokuj MMP"}
                  </MenuItem>
                ) : null}

                {data.related_mmw && data.related_mmw_id ? (
                  <MenuItem
                    as="a"
                    href={`/WMS/documents/doc-mmw/${data.related_mmw_id}`}
                  >
                    Otwórz MMW
                  </MenuItem>
                ) : null}
                {data.related_mmr && data.related_mmr_id ? (
                  <MenuItem
                    as="a"
                    href={`/WMS/documents/doc-mmr/${data.related_mmr_id}`}
                  >
                    Otwórz MMR
                  </MenuItem>
                ) : null}
              </MenuList>
            </Menu>

            <PrintLanguageSelect
              onPrintPL={() =>
                previewDocumentPdf(
                  `/v1/transfers/MMP/${id}/pdf`,
                  `${data.number}.pdf`,
                  "pl",
                )
              }
              onPrintEN={() =>
                previewDocumentPdf(
                  `/v1/transfers/MMP/${id}/pdf`,
                  `${data.number}.pdf`,
                  "en",
                )
              }
            />
          </HStack>
        }
      />

      <Stack
        spacing={6}
        display="flex"
        flexDirection="column"
        flex="1"
        minH={0}
        overflow="hidden"
      >
        {data.status === "partially_confirmed" &&
        data.related_mmr &&
        data.related_mmr_id ? (
          <Alert
            status="warning"
            borderRadius="md"
            alignItems={{ base: "flex-start", md: "center" }}
            flexDirection={{ base: "column", md: "row" }}
            gap={3}
          >
            <AlertIcon />
            <Box flex="1">
              <Text fontWeight="semibold">Towar oczekuje na zwrot</Text>
              <Text fontSize="sm">
                Ilość odrzucona znajduje się w{" "}
                <Badge colorScheme="orange">RETURN</Badge>, a uszkodzona w{" "}
                <Badge colorScheme="purple">USZKODZONE</Badge> na magazynie
                docelowym. Towar jest zarezerwowany przez dokument{" "}
                {data.related_mmr}. Potwierdź MMR, aby zwrócić go na magazyn
                źródłowy.
              </Text>
            </Box>
            <Button
              as="a"
              href={`/WMS/documents/doc-mmr/${data.related_mmr_id}`}
              colorScheme="orange"
              size="sm"
              flexShrink={0}
            >
              Otwórz MMR
            </Button>
          </Alert>
        ) : null}

        <DocumentHeaderSection
          bg={panelBg}
          metaPlacement="right"
          meta={
            <Stack spacing={1} color={labelColor} fontSize="sm">
              <Text>
                Utworzono: {fmtDate(data.created_at)} •{" "}
                {data.owner?.acronym ?? "—"}
              </Text>
              <Text>
                Ostatnia zmiana: {fmtDate(data.updated_at)} •{" "}
                {data.updated_by?.acronym ?? "—"}
              </Text>
              <Text>
                Potwierdzono: {fmtDate(data.confirmed_at)} •{" "}
                {data.confirmed_by?.acronym ?? "—"}
              </Text>
            </Stack>
          }
        >
          <Box
            display="grid"
            gridTemplateColumns={{
              base: "1fr",
              xl: "minmax(0, 1fr) minmax(0, 1fr)",
            }}
            gap={4}
          >
            <Stack spacing={1.5}>
              <DocumentFieldRow label="Z magazynu" labelColor={labelColor}>
                <Text>
                  {data.from_warehouse?.code} — {data.from_warehouse?.name}
                </Text>
              </DocumentFieldRow>
              <DocumentFieldRow label="Na magazyn" labelColor={labelColor}>
                <Text>
                  {data.to_warehouse?.code} — {data.to_warehouse?.name}
                </Text>
              </DocumentFieldRow>
              <DocumentFieldRow label="Typ dokumentu" labelColor={labelColor}>
                <Text>{data.document_type}</Text>
              </DocumentFieldRow>
            </Stack>
            <Stack spacing={1.5}>
              <DocumentFieldRow label="Powiązany MMW" labelColor={labelColor}>
                {data.related_mmw && data.related_mmw_id ? (
                  <Link
                    as="a"
                    color="green.600"
                    href={`/WMS/documents/doc-mmw/${data.related_mmw_id}`}
                  >
                    {data.related_mmw}
                  </Link>
                ) : (
                  <Text>—</Text>
                )}
              </DocumentFieldRow>
              <DocumentFieldRow label="Powiązany MMR" labelColor={labelColor}>
                {data.related_mmr && data.related_mmr_id ? (
                  <Link
                    as="a"
                    color="green.600"
                    href={`/WMS/documents/doc-mmr/${data.related_mmr_id}`}
                  >
                    {data.related_mmr}
                  </Link>
                ) : (
                  <Text>—</Text>
                )}
              </DocumentFieldRow>
            </Stack>
          </Box>
        </DocumentHeaderSection>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderRadius="md"
          p={4}
          display="flex"
          flexDirection="column"
          flex="1"
          minH={0}
          overflow="hidden"
        >
          <TableContainer flex="1" overflowY="auto" minH={0}>
            <Table size="sm" sx={{ tableLayout: "fixed" }} minW="1540px">
              <Thead bg={tableHeadBg} position="sticky" top={0} zIndex={1}>
                <Tr>
                  <Th w="52px">№</Th>
                  <Th w="260px">Produkt (SKU • Nazwa)</Th>
                  <Th w="170px">Partia</Th>
                  <Th w="150px">Miejsce źródłowe</Th>
                  <Th w="150px">Miejsce docelowe</Th>
                  <Th w="90px" isNumeric>
                    Plan
                  </Th>
                  <Th w="90px" isNumeric>
                    Przyjęto
                  </Th>
                  <Th w="90px" isNumeric>
                    Uszk.
                  </Th>
                  <Th w="100px" isNumeric>
                    Odrzucono
                  </Th>
                  <Th w="130px">Przyczyna</Th>
                  <Th w="200px">Komentarz</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data.items ?? []).map((item, index) => {
                  const prod = normalizeProduct(item.product)
                  const row = rows.find((entry) => entry.itemId === item.id)
                  return (
                    <Tr key={item.id}>
                      <Td>{index + 1}</Td>
                      <Td minW={0}>
                        <Stack spacing={1}>
                          <DocumentProductCell
                            sku={prod?.sku}
                            name={prod?.name}
                          />
                          {rowErrors[item.id] ? (
                            <Badge colorScheme="red" width="fit-content">
                              {rowErrors[item.id]}
                            </Badge>
                          ) : null}
                        </Stack>
                      </Td>
                      <Td>
                        <Stack spacing={0.5}>
                          <Text fontSize="sm" color="gray.700">
                            {item.lot_no || "—"}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {item.production_date
                              ? `Prod. ${formatLotDate(item.production_date)}`
                              : "Prod.: —"}
                          </Text>
                          <Text
                            fontSize="xs"
                            color={
                              item.expiration_date ? "orange.600" : "gray.500"
                            }
                          >
                            {item.expiration_date
                              ? `Ważne do ${formatLotDate(item.expiration_date)}`
                              : "Termin: —"}
                          </Text>
                        </Stack>
                      </Td>
                      <Td>{item.location_code || "—"}</Td>
                      <Td verticalAlign="top">
                        <WarehouseLocationSelect
                          warehouseId={data.to_warehouse?.id}
                          productId={prod?.id}
                          value={row?.toLocationCode ?? ""}
                          onChange={(value) =>
                            handleRowChange(item.id, { toLocationCode: value })
                          }
                          title="Wybierz lokalizację docelową"
                          size="sm"
                          isDisabled={data.status !== "draft"}
                        />
                      </Td>
                      <Td isNumeric>{fmtNum(item.qty)}</Td>
                      <Td isNumeric>
                        <Input
                          size="sm"
                          type="number"
                          min={0}
                          isDisabled={data.status !== "draft"}
                          value={row?.acceptedQty ?? "0"}
                          onChange={(event) =>
                            handleRowChange(item.id, {
                              acceptedQty: event.target.value,
                            })
                          }
                        />
                      </Td>
                      <Td isNumeric>
                        <Input
                          size="sm"
                          type="number"
                          min={0}
                          isDisabled={data.status !== "draft"}
                          value={row?.damagedQty ?? "0"}
                          onChange={(event) =>
                            handleRowChange(item.id, {
                              damagedQty: event.target.value,
                            })
                          }
                        />
                      </Td>
                      <Td isNumeric>
                        <Input
                          size="sm"
                          type="number"
                          min={0}
                          isDisabled={data.status !== "draft"}
                          value={row?.rejectedQty ?? "0"}
                          onChange={(event) =>
                            handleRowChange(item.id, {
                              rejectedQty: event.target.value,
                            })
                          }
                        />
                      </Td>
                      <Td>
                        <Select
                          size="sm"
                          isDisabled={data.status !== "draft"}
                          value={row?.discrepancyReason ?? ""}
                          onChange={(event) =>
                            handleRowChange(item.id, {
                              discrepancyReason: event.target.value,
                            })
                          }
                        >
                          {DISCREPANCY_REASON_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </Td>
                      <Td>
                        <Popover placement="left" isLazy>
                          <PopoverTrigger>
                            <Box display="inline-block">
                              <Tooltip
                                label={
                                  row?.discrepancyComment
                                    ? "Edytuj komentarz"
                                    : "Dodaj komentarz"
                                }
                              >
                                <IconButton
                                  aria-label="Komentarz"
                                  icon={<FiMessageSquare />}
                                  size="sm"
                                  variant={
                                    row?.discrepancyComment ? "solid" : "ghost"
                                  }
                                  colorScheme={
                                    row?.discrepancyComment ? "blue" : "gray"
                                  }
                                />
                              </Tooltip>
                            </Box>
                          </PopoverTrigger>
                          <PopoverContent width="300px">
                            <PopoverArrow />
                            <PopoverCloseButton />
                            <PopoverHeader fontSize="sm" fontWeight="bold">
                              Komentarz
                            </PopoverHeader>
                            <PopoverBody>
                              <Textarea
                                size="sm"
                                minH="100px"
                                isDisabled={data.status !== "draft"}
                                placeholder="Wpisz komentarz..."
                                value={row?.discrepancyComment ?? ""}
                                onChange={(event) =>
                                  handleRowChange(item.id, {
                                    discrepancyComment: event.target.value,
                                  })
                                }
                              />
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
      <AlertDialog
        isOpen={isCancelOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setCancelOpen(false)}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader>Anulować dokument MMP?</AlertDialogHeader>
          <AlertDialogBody>
            Dokument MMP zostanie anulowany, a powiązany MMW wróci do wersji
            roboczej.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef as any}
              onClick={() => setCancelOpen(false)}
              variant="ghost"
            >
              Wróć
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              onClick={onCancel}
              isLoading={isCancelling}
              isDisabled={isInventoryBlockedForWarehouses}
              title={
                isInventoryBlockedForWarehouses
                  ? inventoryBlockCancelReason
                  : undefined
              }
            >
              Anuluj dokument
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Container>
  )
}
